import { Component, Input } from '@angular/core';
import { Activity } from '../../core/dto/activity.dtos';

/**
 * Grid editorial de productos — variante "tours boutique" sin carrusel.
 *
 * Diseño:
 *   - Section header opcional con overline + título serif + trust meta
 *   - Primer card "featured" (span 2 cols + 2 rows en desktop) con ribbon
 *   - Resto en grid responsivo
 *   - Hover lift sutil en todos los cards
 *
 * Uso típico:
 *   <app-products-grid
 *       [products]="products"
 *       sectionOverline="Tours"
 *       sectionTitle="Browse our art & culture tours"
 *       [showFeatured]="true"
 *       featuredLabel="Most popular">
 *   </app-products-grid>
 *
 * Breakpoints:
 *   < 576px  → 1 columna (featured ocupa 1 col)
 *   ≥ 576px  → 2 columnas
 *   ≥ 992px  → 3 columnas (featured ocupa 2×2)
 *   ≥ 1500px → 4 columnas (featured ocupa 2×2)
 */
@Component({
    selector: 'app-products-grid',
    templateUrl: './products-grid.component.html',
    styleUrls: ['./products-grid.component.scss'],
})
export class ProductsGridComponent {
    @Input() products: Activity[] = [];

    /** Overline pequeño en mayúsculas arriba del título (ej "Tours") */
    @Input() sectionOverline: string = '';

    /** Título serif de la sección (ej "Browse our art & culture tours") */
    @Input() sectionTitle: string = '';

    /** Bajada/subtítulo opcional debajo del título (frase de marketing o CTA suave) */
    @Input() sectionDescription: string = '';

    /**
     * Sustantivo plural para la meta "N {itemLabel}". Cambia por página:
     *   - Categorías genéricas → "experiences"
     *   - Transfers → "routes"
     *   - Tango / Shore excursions → "shows" / "excursions"
     *   - Nature day trips → "day trips"
     */
    @Input() itemLabel: string = 'experiences';

    /** Si true, muestra rating Tripadvisor global como prueba social */
    @Input() showTrustMeta: boolean = true;

    /** Rating global Signature Tours (queda hardcoded hasta que la API lo unifique) */
    @Input() trustRating: number = 4.7;

    /** Cantidad de reviews global */
    @Input() trustReviewCount: number = 4117;

    /**
     * Si true, el primer card aparece destacado con un ribbon visual.
     * Default: false hasta que tengamos rating real de Tripadvisor por producto
     * (no queremos marcar "Most popular" sin data que lo justifique).
     */
    @Input() showFeatured: boolean = false;

    /** Texto del ribbon en el card featured */
    @Input() featuredLabel: string = 'Most popular';

    trackById(_: number, p: Activity): number | string {
        return p?.id ?? _;
    }

    get hasHeader(): boolean {
        return !!(this.sectionOverline || this.sectionTitle || this.sectionDescription);
    }

    get itemCount(): number {
        return this.products?.length || 0;
    }

    /**
     * Cuando hay ≤3 productos, el grid normal de 4 cols deja huecos a la derecha.
     * Activamos un modo "compact" que cambia el contenedor a flex centrado.
     * Si vienen más productos, vuelve automáticamente al grid normal.
     */
    get isCompact(): boolean {
        return this.itemCount > 0 && this.itemCount <= 3;
    }
}
